import { api } from './api';

export type PushSupport =
  | 'ok'
  /** iPhone/iPad: Web Push works only in the app added to the Home Screen (iOS 16.4+). */
  | 'ios-install'
  | 'denied'
  | 'unsupported'
  /** Plain http (e.g. http://IP:port): browsers allow notifications only over https. */
  | 'insecure'
  /** No service worker (the Vite dev server): notifications need the built app. */
  | 'no-worker';

const isIos = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.userAgent.includes('Mac') && navigator.maxTouchPoints > 1);
const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

export function pushSupport(): PushSupport {
  if (!window.isSecureContext) return 'insecure';
  const capable =
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (isIos() && !isStandalone()) return 'ios-install';
  if (!capable) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  return 'ok';
}

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const raw = atob((base64url + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

/** Only what the server needs, whatever extra fields this browser's toJSON() adds. */
function subscriptionBody(sub: PushSubscription) {
  const json = sub.toJSON();
  return {
    endpoint: sub.endpoint,
    keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
  };
}

const sameKey = (sub: PushSubscription, publicKey: string) => {
  const current = sub.options.applicationServerKey;
  if (!current) return true;
  const a = new Uint8Array(current);
  const b = keyBytes(publicKey);
  return a.length === b.length && a.every((v, i) => v === b[i]);
};

/** Like `pushSupport`, but also checks that the service worker is installed. */
export async function pushSupportAsync(): Promise<PushSupport> {
  const basic = pushSupport();
  if (basic !== 'ok' && basic !== 'denied') return basic;
  const reg = await navigator.serviceWorker.getRegistration();
  return reg ? basic : 'no-worker';
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator)) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

/**
 * Asks for permission and registers this device. Safari allows the permission prompt only
 * directly in a tap, so the caller starts `Notification.requestPermission()` synchronously in
 * the click handler and passes the promise in.
 */
export async function enablePush(
  publicKey: string,
  permissionRequest: Promise<NotificationPermission> = Notification.requestPermission(),
): Promise<'ok' | 'denied'> {
  const permission = await permissionRequest;
  if (permission !== 'granted') return 'denied';
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) throw new Error('no-worker');
  let sub = await reg.pushManager.getSubscription();
  // Keys changed on the server (e.g. a new database): subscribe again.
  if (sub && !sameKey(sub, publicKey)) {
    await sub.unsubscribe();
    sub = null;
  }
  sub ??= await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: keyBytes(publicKey),
  });
  await api.pushSubscribe(subscriptionBody(sub));
  return 'ok';
}

export async function disablePush(): Promise<void> {
  const sub = await currentSubscription();
  if (!sub) return;
  await api.pushUnsubscribe(sub.endpoint).catch(() => undefined);
  await sub.unsubscribe();
}

/**
 * On start: make sure the server still knows this device (e.g. after a restore) and that it uses
 * the current server key. Silent; does nothing without permission.
 */
export async function syncPushSubscription(): Promise<void> {
  if (pushSupport() !== 'ok' || Notification.permission !== 'granted') return;
  const sub = await currentSubscription();
  if (!sub) return;
  const state = await api.push();
  if (!sameKey(sub, state.publicKey)) {
    await enablePush(state.publicKey);
  } else if (!state.devices.some((d) => d.endpoint === sub.endpoint)) {
    await api.pushSubscribe(subscriptionBody(sub));
  }
}

/** Closes notifications about an occurrence once it is checked off in the app. */
export async function closeNotificationsFor(itemId: string, date: string): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg || !('getNotifications' in reg)) return;
  for (const n of await reg.getNotifications()) {
    const check = (n.data as { check?: { itemId?: string; date?: string } } | null)?.check;
    if (check?.itemId === itemId && check.date === date) n.close();
  }
}

/** What a delivery problem means, in plain words (shown in Settings). */
export function pushProblem(error: string | null, status: number | null = null): string {
  const code = status ?? (error ? Number.parseInt(error, 10) : Number.NaN);
  if (Number.isNaN(code)) {
    return 'Strežnik ne doseže storitve za obvestila (internet ali požarni zid na strežniku).';
  }
  if (code === 401 || code === 403) {
    return 'Storitev je zavrnila podpis strežnika. Na tej napravi izklopi in znova vklopi obvestila.';
  }
  if (code === 404 || code === 410) return 'Naprava ni več prijavljena. Vklopi obvestila znova.';
  if (code === 413) return 'Sporočilo je preveliko.';
  if (code === 429) return 'Preveč obvestil naenkrat. Poskusi čez nekaj minut.';
  if (code >= 500) return 'Storitev za obvestila trenutno ne deluje. Poskusi kasneje.';
  return `Napaka pri pošiljanju (${error ?? code}).`;
}
